import { useRef, useState } from 'react';
import { Icon } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/hooks/use-app-context';
import { ModelPerformanceHeader } from './ModelPerformanceHeader';
import { SegmentControl } from './SegmentControl';
import { SessionNav } from './SessionNav';
import { Field } from './Field';
import type { DiffSegment } from '@/utils/metrics';

interface ASRScreenProps {
  language: string;
  model: string;
  asrInputMode: 'text' | 'audio' | 'record';
  asrInputText: string;
  asrAudioUrl: string;
  isTranscribing: boolean;
  transcriptionResult: string;
  referenceText: string;
  wer: number | null;
  cer: number | null;
  diffSegments: DiffSegment[];
  isRecording: boolean;
  onSetAsrInputMode: (mode: 'text' | 'audio' | 'record') => void;
  onSetAsrInputText: (text: string) => void;
  onSetAsrAudioUrl: (url: string) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onTranscribe: () => void;
  onReset: () => void;
  onBack: () => void;
}

export function ASRScreen({
  language,
  model,
  asrInputMode,
  asrInputText,
  asrAudioUrl,
  isTranscribing,
  transcriptionResult,
  referenceText,
  wer,
  cer,
  diffSegments,
  isRecording,
  onSetAsrInputMode,
  onSetAsrInputText,
  onSetAsrAudioUrl,
  onStartRecording,
  onStopRecording,
  onTranscribe,
  onReset,
  onBack,
}: ASRScreenProps) {
  const { t } = useApp();
  const tModel = t.modelPerformancePage;

  const canTranscribe =
    (asrInputMode === 'text' && Boolean(asrInputText)) ||
    ((asrInputMode === 'audio' || asrInputMode === 'record') && Boolean(asrAudioUrl));

  return (
    <div className="flex w-full flex-col gap-6 sm:gap-8">
      <ModelPerformanceHeader model={model} language={language} currentIndex={0} total={1} progressPercent={0} onBack={onBack} />

      {/* Input mode */}
        <section className="py-5 sm:py-7">
        <p className="flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.26em] text-primary/60">
          {tModel.asrInputModeTitle}
        </p>
        <div className="mt-4">
          <SegmentControl
            options={[
              { value: 'record' as const, label: tModel.asrRecordMode },
              { value: 'text' as const, label: tModel.asrTextMode },
              { value: 'audio' as const, label: tModel.asrAudioMode },
            ]}
            value={asrInputMode}
            onChange={onSetAsrInputMode}
          />
        </div>

        <div className="mt-6 space-y-4">
          {asrInputMode === 'record' && (
            <>
              <Field label={tModel.asrReferenceTextLabel}>
                <Textarea
                  value={asrInputText}
                  onChange={(e) => onSetAsrInputText(e.target.value)}
                  placeholder={tModel.asrReferenceTextPlaceholder}
                  rows={3}
                  className="min-h-[80px] resize-none rounded-2xl border-border/40 bg-background/60 px-4 py-3 text-sm text-foreground shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-primary/30"
                />
              </Field>

              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/40 p-8">
                {isRecording ? (
                  <div className="space-y-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-bold text-destructive">
                      <div className="h-3 w-3 rounded-full bg-destructive animate-pulse" />
                      {tModel.recordingInProgress}
                    </div>
                    <Button variant="destructive" onClick={onStopRecording} className="h-11 w-full max-w-48 rounded-full text-sm font-semibold">
                      <Icon name="square" className="mr-2 h-4 w-4" />
                      {tModel.stopRecording}
                    </Button>
                  </div>
                ) : (
                  <Button onClick={onStartRecording} variant={asrAudioUrl ? 'outline' : 'default'} className="h-11 w-full max-w-48 rounded-full text-sm font-semibold">
                    <Icon name="mic" className="mr-2 h-4 w-4" />
                    {asrAudioUrl ? tModel.restartRecording : tModel.recordAudio}
                  </Button>
                )}
                {asrAudioUrl && !isRecording && (
                  <div className="mt-4 w-full max-w-md">
                    <audio controls src={asrAudioUrl} className="w-full" />
                    <p className="mt-2 text-center text-xs text-muted-foreground/70">{tModel.audioReadyForTranscription}</p>
                  </div>
                )}
              </div>
            </>
          )}

          {asrInputMode === 'text' && (
            <Field label={tModel.asrTextInputLabel}>
              <Textarea
                value={asrInputText}
                onChange={(e) => onSetAsrInputText(e.target.value)}
                placeholder={tModel.asrTextInputPlaceholder}
                rows={4}
                className="min-h-[100px] resize-none rounded-2xl border-border/40 bg-background/60 px-4 py-3 text-sm text-foreground shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-primary/30"
              />
              <p className="mt-1 text-xs text-muted-foreground/70">{tModel.asrTextInputHint}</p>
            </Field>
          )}

          {asrInputMode === 'audio' && (
            <>
              <Field label={tModel.importAudioFile}>
                <Input type="file" accept="audio/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) onSetAsrAudioUrl(URL.createObjectURL(file)); }} className="h-11 cursor-pointer rounded-2xl border-border/40 bg-background/60 text-sm" />
                <p className="mt-1 text-xs text-muted-foreground/70">{tModel.supportedAudioFormats}</p>
              </Field>
              <Field label={tModel.audioUrlLabel}>
                <Input value={asrAudioUrl} onChange={(e) => onSetAsrAudioUrl(e.target.value)} placeholder="https://example.com/audio.wav" className="h-11 rounded-2xl border-border/40 bg-background/60 px-4 text-sm text-foreground placeholder:text-muted-foreground/50" />
                {asrAudioUrl && <audio controls src={asrAudioUrl} className="mt-2 w-full" />}
              </Field>
            </>
          )}
        </div>

        <Button
          onClick={onTranscribe}
          disabled={isTranscribing || !canTranscribe}
          className="mt-6 h-11 w-full rounded-full sm:w-auto border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90 disabled:border-border/30 disabled:bg-background/40 disabled:text-muted-foreground"
        >
          {isTranscribing ? (
            <><Icon name="loader2" className="mr-2 h-4 w-4 animate-spin" />{tModel.transcribing}</>
          ) : (
            <><Icon name="mic" className="mr-2 h-4 w-4" />{tModel.transcribe}</>
          )}
        </Button>
      </section>

      {/* Results */}
      {transcriptionResult && (
      <section className="py-5 sm:py-7">
          <p className="flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.26em] text-primary/60">
            {tModel.transcriptionResultTitle}
          </p>
          <div className="mt-4 rounded-2xl border border-border/40 bg-background/60 p-5">
            <p className="text-lg font-medium text-foreground">{transcriptionResult}</p>
          </div>

          {(referenceText || (asrInputMode === 'record' && asrInputText)) && (
            <div className="mt-6 space-y-6">
              {/* Metrics */}
              <div className="flex gap-3">
                <span className="rounded-full border border-border/40 bg-background/60 px-4 py-1.5 text-xs font-semibold text-foreground">
                  WER: {wer !== null ? wer.toFixed(2) : '—'}%
                </span>
                <span className="rounded-full border border-border/40 bg-background/60 px-4 py-1.5 text-xs font-semibold text-foreground">
                  CER: {cer !== null ? cer.toFixed(2) : '—'}%
                </span>
              </div>

              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <div>
                  <p className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/50">{tModel.referenceLabel}</p>
                  <p className="mt-1 rounded-2xl border border-border/40 bg-background/60 p-4 text-sm text-foreground">{referenceText || asrInputText}</p>
                </div>
                <div>
                  <p className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/50">{tModel.transcriptionLabel}</p>
                  <p className="mt-1 rounded-2xl border border-border/40 bg-background/60 p-4 text-sm text-foreground">{transcriptionResult}</p>
                </div>
              </div>

              {/* Diff */}
              {diffSegments.length > 0 && (
                <div>
                  <p className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/50">{tModel.errorDetailsLabel}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {diffSegments.map((diff, idx) => (
                      <span
                        key={idx}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                          diff.type === 'match' ? 'bg-success/10 text-success dark:text-success' :
                          diff.type === 'substitution' ? 'bg-brand/10 text-brand dark:text-brand' :
                          diff.type === 'insertion' ? 'bg-info/10 text-info dark:text-info' :
                          'bg-danger/10 text-danger dark:text-danger'
                        }`}
                        title={diff.type}
                      >
                        {diff.type === 'deletion' ? <span className="line-through">{diff.original}</span> :
                         diff.type === 'insertion' ? <span className="underline">{diff.generated}</span> :
                         diff.type === 'substitution' ? <span>{diff.original} → {diff.generated}</span> :
                         diff.original}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-4 text-xs text-muted-foreground/70">
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-success" />{tModel.diffCorrect}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-primary" />{tModel.diffSubstitution}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-info" />{tModel.diffInsertion}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-danger" />{tModel.diffDeletion}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <SessionNav onNext={onReset} />
    </div>
  );
}
